<template>
	<div ref="color_picker">
		<v-tooltip bottom>
			<template v-slot:activator="{on}">
				<v-btn v-on="on" :style="button_css" class="k-tight-btn" @click="show_color_picker = !show_color_picker">
					{{ show_color_picker ? "Close Color Picker" : "Choose Color" }}
				</v-btn>
			</template>
			{{ show_color_picker ? "Close Color Picker" : "Open Color Picker" }}
		</v-tooltip>
		<div :style=color_picker_style>
			<v-color-picker class="k-color-picker elevation-5" :class="default_color?'pb-10':''" v-if="show_color_picker" @update:color="update_color" v-model="picker_color" :swatches="swatches" show-swatches mode="hexa" width="360" right hide-mode-switch></v-color-picker>
			<div class="k-wcag-box" v-if="show_color_picker">
				<v-tooltip bottom>
					<template v-slot:activator="{on}">
						<v-icon small v-on="on" :style="wcag_css">{{is_wcag_compliant ? 'fas fa-check' : 'fas fa-x'}}</v-icon>
					</template>
					{{ is_wcag_compliant ? 'WCAG AA Compliant' : 'Not WCAG AA Compliant' }}
				</v-tooltip>
			</div>
			<div v-if="show_color_picker && default_color" class="k-default-color-btn-wrapper">
				<v-btn class="k-default-color-btn k-tight-btn" small @click="use_default_color" :style="{'background-color': default_color, 'color': U.get_contrast_color(default_color)}">Use default subject color</v-btn>
			</div>
		</div>
	</div>
</template>

<script>

import { mapState } from 'vuex'
export default {
	props: {
		// collection: {type: Object, required: true },
		color: {type: String, required: true},
		title: {type: String, required: false},
		subject: {type: String, required: true}
	},
	data() { return {
		show_color_picker: false,
		picker_color: this.color,
		default_color: '',
		color_explicitly_changed: false
	}},
	mounted() {
		if (this.color_is_auto_generated) {
			// Check if there is a subject, default to that
			if (this.subject && this.subject in this.subjects) {
				this.color_picker = this.subjects[this.subject].color
			// If there is no subject, but there is a title, then the color was auto-generated using this logic, 
			// we will need to match the picker_color to this
			} else if (this.title) {
				let n = 2
				for (let i = 0; i < this.title.length; ++i) {
					n += this.title.charCodeAt(i)
				}
				n = n * 1
				n = (n % 16) + 1
				this.picker_color = this.site_config.swatches[n-1][0]
			// If there is no title and no subject (like creating a new LP), then we can default the picker color to a random swatch color
			} else {
				this.picker_color = this.swatches[Math.floor(Math.random()*this.swatches.length)][0]
			}
		} else this.picker_color = this.color
		if (this.subject && this.subject in this.subjects) this.default_color = this.subjects[this.subject].color
		// If there is already a color assigned to it, then the color has been explicitly changed (true for all existing courses)
		if (this.subject) this.color_explicitly_changed = true
		this.$emit('update_color', this.picker_color)
		document.addEventListener('click', this.outside_clicked);
	},
	watch: {
		subject(new_subject, old_subject) { 
				if (new_subject === '-') this.default_color = ''
				else this.default_color = this.subjects[new_subject].color
				if (!this.color_explicitly_changed) {
					if (new_subject in this.subjects) this.picker_color = this.subjects[new_subject].color
					else this.picker_color = this.subjects[old_subject].color
					this.update_color({hex: this.picker_color}, false)
				}
		},
	},
	beforeDestroy() {
		document.removeEventListener('click', this.outside_clicked);
	},
	computed: {
		...mapState(['subjects', 'site_config']),
		color_is_auto_generated() {
			return this.color === ''
		},
		is_wcag_compliant() {
			const lightened_color = U.shade_color(this.picker_color, 'light')
			const dark_color = U.shade_color(this.picker_color, 'dark')
			const darkest_color = U.shade_color(this.picker_color, 'darkest')
			return this.get_contrast_ratio(this.picker_color) && this.get_contrast_ratio(lightened_color) && this.get_contrast_ratio(dark_color) && this.get_contrast_ratio(darkest_color)
		},
		swatches() {
			return this.site_config.swatches
		},
		button_css() {
			return {
				backgroundColor: this.picker_color,
				color: U.get_contrast_color(this.picker_color),
				// boxShadow: '0 0 0 1px #000 !important',
				width: '170px'
			}
		},
		wcag_css() {
			return this.is_wcag_compliant ? {color: '#00AD00'} : {color: 'red'}
		},
		color_picker_style() {
			return {
				position: 'absolute',
				zIndex: 999999,
				// Adjust the positioning dynamically based on button position
				'margin-left': this.title ? '-95px' : '-160px'
			};
		},
	},
	methods: {
		get_contrast_ratio(color) {
			let r = parseInt(color.substr(1, 2), 16);
			let g = parseInt(color.substr(3, 2), 16);
			let b = parseInt(color.substr(5, 2), 16);

			let a = [r, g, b].map(function (v) {
				v /= 255;
				return v <= 0.03928
				? v / 12.92
				: Math.pow( (v + 0.055) / 1.055, 2.4 );
			});
			const color1luminance = U.get_contrast_color(color) === '#FFFFFF' ? 1.0 : 0.0
			const color2luminance = a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
			const brightest = Math.max(color1luminance, color2luminance);
			const darkest = Math.min(color1luminance, color2luminance);
			const contrast_ratio = (brightest + 0.05) / (darkest + 0.05);
			return contrast_ratio > 4.5
		},
		update_color(new_color, color_explicitly_changed = true) {
			this.$emit('update_color', new_color.hex, this.subject)
			if (color_explicitly_changed) this.color_explicitly_changed = true
		},
		outside_clicked(event) {
			if (this.$refs.color_picker && !this.$refs.color_picker.contains(event.target)) {
				this.show_color_picker = false
      		}
		},
		use_default_color() {
			this.picker_color = this.default_color
			this.$emit('update_color', this.picker_color, this.subject)
		}
	}
}
</script>

<style lang="scss">
.k-color-picker {
	margin-top: 6px;
	.v-color-picker__input span { display:none; }
}
.k-wcag-box {
	position:absolute;
    top:229px;
    right:80px;
}
.k-default-color-btn-wrapper {
	position:absolute;
    left:0px;
	bottom: 16px;
	width:100%;
	text-align:center;
}
.k-default-color-btn {
	font-size: 12px !important;
}
</style>
